import { defineStore } from "pinia";
import { ref } from "vue";
import Echo from "laravel-echo";
import { useGlobalUser } from "@/stores/globalUser";
import Pusher from "pusher-js";
import { buildEchoConfig } from "@/modules/ws";
import {
  FollowOrAdmireNotifyData,
  ImageTaskNotifyData,
  NotifyData,
  PopupNotificationItem,
  ReplyNotifyData,
  StoryGenNotifyData,
  StoryVideoNotifyData,
  WsNotification,
} from "@/types/notification";
import { useNotification } from "@/stores/notification";
import notificationUtils from "@/stores/notification-utils";
import { useMateCommit } from "@/stores/mateCommit";
import { useFollow } from "@/stores/follow";
import { useAdmire } from "@/stores/admire";
import { useStory } from "@/stores/story";
import { useMate } from "@/stores/mate";
import { useTask } from "@/stores/task";
import { useMateTrait } from "./mateTrait";
import { PusherPrivateChannel } from "laravel-echo/dist/channel";
import { NotificationTypeEnum } from "@/enums/notificationEnum";
import { useInteractiveReview } from "./interactive-review";
import { useGenerate } from "./generate";
import { useReply } from "./reply";

const DEFAULT_POPUP: PopupNotificationItem = {
  show: false,
  id: "",
  type: 4,
  title: "",
  content: "",
  avatarUrl: "",
  quote: "",
  mateName: "",
  onClick: () => {},
};

// 不在通知列表中的通知类型
export const TYPE_NOT_IN_NOTIFICATION_LIST: NotificationTypeEnum[] = [
  NotificationTypeEnum.TextImageFinished,
  NotificationTypeEnum.MomentCommitRequested,
  NotificationTypeEnum.NewChatMessage,
  NotificationTypeEnum.StoryGenerated,
  NotificationTypeEnum.VideoCreated,
];

export const usePushChannel = defineStore("pushChannel", () => {
  const socket = ref<Echo | null>(null);
  const popup = ref<PopupNotificationItem>(DEFAULT_POPUP);
  const globalUser = useGlobalUser();
  const notification = useNotification();
  const mateStore = useMate();

  function onMsgInNotificationList(n: WsNotification<NotifyData>) {
    // 维护未读消息数和未读消息列表的changed状态
    if (TYPE_NOT_IN_NOTIFICATION_LIST.includes(n.type)) {
      return;
    }
    notification.mateId2UnreadCount[n.recipientMateId]++;
    if (globalUser.currentMateId === n.recipientMateId) {
      notification.unread.changed = true;
    }
  }

  function onNotification(n: WsNotification<NotifyData>): void {
    onMsgInNotificationList(n);
    // 刷新相关Store状态
    switch (n.type) {
      case 4:
      case 5:
        onMomentRepliedNotification(n as WsNotification<ReplyNotifyData>);
        break;
      case 7: // 图片生成完成
        onImageTaskNotification(n as WsNotification<ImageTaskNotifyData>);
        break;
      case 8: // 为Ta创作审核完成
        onCommitNotification();
        break;
      case 10: // 关注/喜欢
      case 11:
        onFollowOrAdmireNotification(
          n as WsNotification<FollowOrAdmireNotifyData>
        );
        break;
      case 12: // 为Ta创作审核
        onCommitReviewNotification();
        break;
      case 13: // 故事生成完成
        onStoryGenNotification(n as WsNotification<StoryGenNotifyData>);
        break;
      case 15: // 视频生成完成
        onVideoTaskNotification(n as WsNotification<StoryVideoNotifyData>);
        break;
      case 21: // 资深读者申请通过
        onBecomeSeniorReaderNotification();
        break;
      case 34: // 角色被领养
        onMateAdoptedNotification();
        break;
      case 35: // 新站内信
        onNewSiteMessageNotification();
        break;
      case 36: // 用户分享奖励
        onUserShareRewardNotification();
        break;
      case 39: // 角色形象生成完成
        onSpriteGeneratedNotification();
        break;
      case 38: // 互动事件审核
      case 37: // 收到Mail
      case 32:
        onInteractiveEventReviewNotification();
        break;
    }

    // 通知弹窗处理逻辑
    const sn = notificationUtils.standardNotificationItemFactory(n);
    if (n.recipientMateId !== globalUser.currentMateId) {
      const mate = mateStore.getMyMateById(n.recipientMateId);
      sn.mateName = mate?.name || "";
    }
    const clickHandler = sn.onClick;
    sn.onClick = async (router) => {
      if (n.recipientMateId !== globalUser.currentMateId) {
        await mateStore.changeCurrentMate(n.recipientMateId);
      }
      clickHandler(router);
      if (!TYPE_NOT_IN_NOTIFICATION_LIST.includes(n.type)) {
        notification.readNotification(n.id);
      }
    };
    popup.value = { show: true, ...sn };
    const { taskType } = (n.data as ImageTaskNotifyData) || {};
    if (taskType === "trait") useMateTrait().getOrFetchTraitList(true);
  }

  function startPusherListen(): void {
    window.Pusher = Pusher;
    socket.value = new Echo(buildEchoConfig());
    const privateChannel = socket.value.private(
      `user.${globalUser.id}`
    ) as PusherPrivateChannel;
    privateChannel.notification((n: WsNotification<NotifyData>) => {
      onNotification(n);
    });
  }

  function onMomentRepliedNotification(n: WsNotification<ReplyNotifyData>) {
    useReply().getOrFetchReplyList(n.data.rootId, true);
  }

  async function onImageTaskNotification(
    n: WsNotification<ImageTaskNotifyData>
  ) {
    useGenerate().onTaskFinished(n.data.taskId);
    // useTask().onTaskFinished("image", n.data.textImageId);
  }

  async function onVideoTaskNotification(
    n: WsNotification<StoryVideoNotifyData>
  ) {
    useTask().onTaskFinished("video", n.data.storyId);
  }

  function onCommitNotification(): void {
    const commit = useMateCommit();
    commit.pending.changed = true;
    commit.finished.changed = true;
  }

  function onFollowOrAdmireNotification(
    n: WsNotification<FollowOrAdmireNotifyData>
  ): void {
    if (n.type === 10) {
      const follow = useFollow();
      follow.follower.changed = true;
    } else {
      const admire = useAdmire();
      admire.admirer.changed = true;
    }
  }

  function onCommitReviewNotification() {
    const commit = useMateCommit();
    commit.pendingCommit.changed = true;
    commit.finishedCommit.changed = true;
  }

  async function onBecomeSeniorReaderNotification() {
    const user = useGlobalUser();
    await user.refreshUser();
  }

  async function onMateAdoptedNotification() {
    await mateStore.fetchMateList(true);
  }

  async function onNewSiteMessageNotification() {}

  async function onUserShareRewardNotification() {
    const user = useGlobalUser();
    await user.refreshUser();
  }

  async function onStoryGenNotification(n: WsNotification<StoryGenNotifyData>) {
    const story = useStory();
    await story.storyGenerated(n.data.uuid);
  }

  async function onInteractiveEventReviewNotification() {
    const review = useInteractiveReview();
    const notificationStore = useNotification();
    notificationStore.initSummaryCount();
    const routeName = window.$router.currentRoute.value.name;
    if (routeName === "interactive") {
      await review.initOrFetchList(null, true);
    } else if (routeName === "interactionReview") {
      await review.initOrFetchList(false, true);
    }
  }

  async function onSpriteGeneratedNotification() {
    await mateStore.fetchMateList(true);
  }

  function resetPopup(): void {
    popup.value = Object.assign({}, DEFAULT_POPUP);
  }

  return {
    socket,
    popup,
    startPusherListen,
    onNotification,
    resetPopup,
  };
});
